import { allocateQueueIdentity, type QueueIdentity } from "./queueIdentity";

/** Starter commands are durable before local projection changes; receipts follow reconnect. */
export type RunOp =
  | "jam-append"
  | "start"
  | "end"
  | "focus"
  | "attempt-advance"
  | "attempt-revert"
  | "fall"
  | "status"
  | "excuse"
  | "abandon"
  | "pause"
  | "resume"
  | "adjust";
export interface RunOperation {
  op: RunOp;
  heat_id: string;
  payload: Record<string, unknown>;
  client_id: string;
  client_device: string;
  client_seq: number;
  client_submitted_at: string;
}
export interface RunReceipt {
  client_id: string;
  result: "ok" | "noop" | "conflict";
  reason?: string;
  server_state?: {
    id: string;
    status: string;
    version: number;
    current_attempts: Record<string, number>;
  };
}
interface Data {
  device: string;
  seq: number;
  scopes: Record<string, RunOperation[]>;
}
export interface RunStore {
  identity?: () => Promise<QueueIdentity>;
  update<T>(fn: (data: Data) => T): Promise<T>;
}
const initial = (): Data => ({ device: crypto.randomUUID(), seq: 0, scopes: {} });
export function indexedRunStore(): RunStore {
  let opening: Promise<IDBDatabase> | undefined;
  const database = () =>
    (opening ??= new Promise((resolve, reject) => {
      const request = indexedDB.open("scorio.run-queue.v1", 1);
      request.onupgradeneeded = () => request.result.createObjectStore("state");
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    }));
  return {
    identity: allocateQueueIdentity,
    async update<T>(fn: (data: Data) => T) {
      const db = await database();
      return new Promise<T>((resolve, reject) => {
        const tx = db.transaction("state", "readwrite");
        const object = tx.objectStore("state");
        const read = object.get("queue");
        let result: T;
        read.onsuccess = () => {
          try {
            const data: Data = read.result ?? initial();
            result = fn(data);
            object.put(data, "queue");
          } catch (error) {
            tx.abort();
            reject(error);
          }
        };
        tx.oncomplete = () => resolve(result);
        tx.onerror = () => reject(tx.error ?? new Error("Queue storage failed"));
        tx.onabort = () => reject(tx.error ?? new Error("Queue storage failed"));
      });
    },
  };
}
export function createRunQueue(
  scope: { principal: string; competitionId: string },
  options: {
    store: RunStore;
    send: (batch: { client_now: string; ops: RunOperation[] }) => Promise<RunReceipt[]>;
  },
) {
  const key = JSON.stringify([scope.principal, scope.competitionId]);
  let flight: Promise<RunReceipt[]> | undefined;
  const items = () =>
    options.store.update((data) =>
      [...(data.scopes[key] ?? [])].sort((a, b) =>
        a.client_device === b.client_device
          ? a.client_seq - b.client_seq
          : Date.parse(a.client_submitted_at) - Date.parse(b.client_submitted_at),
      ),
    );
  return {
    items,
    async enqueue(op: RunOp, heat_id: string, payload: Record<string, unknown> = {}) {
      const identity = await options.store.identity?.();
      return options.store.update((data) => {
        const queue = (data.scopes[key] ??= []);
        if (queue.length >= 1000)
          throw new Error("Queue full. Reconnect before adding more actions.");
        const item: RunOperation = {
          op,
          heat_id,
          payload,
          client_id: crypto.randomUUID(),
          client_device: identity?.client_device ?? data.device,
          client_seq: identity?.client_seq ?? ++data.seq,
          client_submitted_at: new Date().toISOString(),
        };
        queue.push(item);
        return item;
      });
    },
    flush(): Promise<RunReceipt[]> {
      if (flight) return flight;
      flight = (async () => {
        const receipts: RunReceipt[] = [];
        while (true) {
          const batch = (await items()).slice(0, 50);
          if (!batch.length) return receipts;
          const result = await options.send({ client_now: new Date().toISOString(), ops: batch });
          const expected = new Set(batch.map((item) => item.client_id));
          if (
            !Array.isArray(result) ||
            result.length !== batch.length ||
            result.some((receipt) => {
              if (
                !receipt ||
                !expected.delete(receipt.client_id) ||
                !["ok", "noop", "conflict"].includes(receipt.result)
              )
                return true;
              if (receipt.result !== "conflict") return false;
              const state = receipt.server_state;
              const op = batch.find((op) => op.client_id === receipt.client_id);
              return (
                !state ||
                state.id !== op?.heat_id ||
                !["pending", "live", "complete", "abandoned"].includes(state.status) ||
                !Number.isInteger(state.version) ||
                state.version < 0 ||
                !state.current_attempts ||
                typeof state.current_attempts !== "object"
              );
            })
          )
            throw new Error("Invalid replay receipt. Actions remain queued.");
          await options.store.update((data) => {
            const accepted = new Set(
              result
                .filter((r) => r.result === "ok" || r.result === "noop")
                .map((r) => r.client_id),
            );
            // Conflict discards only that heat's local state, including later staged ops.
            const conflicts = new Set(
              result
                .filter((r) => r.result === "conflict")
                .map((r) => batch.find((b) => b.client_id === r.client_id)?.heat_id),
            );
            data.scopes[key] = (data.scopes[key] ?? []).filter(
              (item) => !accepted.has(item.client_id) && !conflicts.has(item.heat_id),
            );
          });
          receipts.push(...result);
        }
      })().finally(() => {
        flight = undefined;
      });
      return flight;
    },
  };
}
